import { ResizeObserver } from "@juggle/resize-observer";
import React from "react";

type OnResizeHandler = () => void;

export const ChildrenResizeSensor = (props: {
    onResize: OnResizeHandler;
    children?: React.ReactNode;
}): React.ReactNode => {
    const wrapperRef = React.useRef<HTMLDivElement | null>(null);
    const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
    const latestOnResizeRef = React.useRef(props.onResize);
    latestOnResizeRef.current = props.onResize;

    // Create the observer once on mount so we keep the same lifecycle as the
    // original class component and avoid missing the initial ref callback.
    React.useEffect(() => {
        const observer = new ResizeObserver((_entries) => {
            latestOnResizeRef.current();
        });
        resizeObserverRef.current = observer;

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current);
        }

        return () => {
            if (wrapperRef.current) {
                observer.unobserve(wrapperRef.current);
            }
            observer.disconnect();
            resizeObserverRef.current = null;
        };
        // The observer intentionally mirrors the mount-only lifecycle of the
        // prior class implementation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep memoized: React ref callbacks require stable identity to prevent unnecessary
    // reconnections. Without useCallback, ref would be recreated every render, causing
    // the ResizeObserver to disconnect and reconnect repeatedly.
    const setWrapperRef = React.useCallback((elem: HTMLDivElement | null) => {
        // Clean up observer for previous element
        if (wrapperRef.current && resizeObserverRef.current) {
            resizeObserverRef.current.unobserve(wrapperRef.current);
        }

        // Set up observer for new element
        if (elem && resizeObserverRef.current) {
            resizeObserverRef.current.observe(elem);
        }

        wrapperRef.current = elem;
    }, []);

    return <div ref={setWrapperRef}>{props.children}</div>;
};
